import { Pagination } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { useMemo, useState } from "react";
import { AutoLoadControl } from "./AutoLoadControl";
import "./index.css";

export interface PaginationFooterProps {
  current: number;
  pageSize: number;
  total: number;
  loadedCount: number;
  cachedPages: number[];
  loadIcon?: React.ReactNode;
  loading: boolean;
  onChange: (page: number) => void;
  // 自动加载相关
  autoLoading?: boolean;
  loadingProgress?: {
    currentPage: number;
    totalPages: number;
    percentage: number;
  };
  onLoadNextPage?: () => void;
  onStartAutoLoad?: (loadNextPages: number[]) => void;
  onStopLoading?: () => void;
}

export const PaginationFooter = ({
  current,
  loadIcon,
  pageSize,
  total,
  cachedPages,
  loadedCount,
  autoLoading = false,
  loadingProgress,
  onChange,
  onLoadNextPage,
  onStartAutoLoad,
  onStopLoading,
}: PaginationFooterProps) => {
  const [batchSize, setBatchSize] = useState(5);
  const totalPages = Math.ceil(total / pageSize);

  const hideAutoLoadControl = useMemo(() => {
    return cachedPages.length >= totalPages;
  }, [cachedPages, totalPages]);

  // 计算下一次需要加载的页面数组
  const calculateNextPagesToLoad = () => {
    const unloadPages = Array.from(
      { length: totalPages },
      (_, i) => i + 1
    ).filter((page) => !cachedPages.includes(page));
    return unloadPages.slice(0, batchSize);
  };

  // 自定义分页器的itemRender，为未加载的页码添加右下角红点+灰色样式
  const itemRender = (
    page: number,
    type: "page" | "prev" | "next" | "jump-prev" | "jump-next",
    originalElement: React.ReactNode
  ) => {
    const isLoaded = cachedPages.includes(page);
    if (
      type === "prev" ||
      type === "next" ||
      type === "jump-prev" ||
      type === "jump-next"
    ) {
      return <div className="pageBox">{originalElement}</div>;
    }
    return (
      <div
        className="pageBox"
        style={{
          backgroundColor: current === page ? "#2563EB" : "#f5f5f5",
          color: current === page ? "#fff" : isLoaded ? "#171717" : "#a3a3a3",
        }}
      >
        {page}
        {!isLoaded && <span className="unload_spot" />}
      </div>
    );
  };

  return (
    <div className={"pagination_footer"}>
      <div className="pagination_leftWrap">
        {/* 已加载数据统计 - 始终显示 */}
        <span className={"pagination_total"}>
          <InfoCircleOutlined
            style={{ color: "#2563EB", marginRight: "4px" }}
          />
          已加载: {loadedCount}/{total}条数据
        </span>

        {!hideAutoLoadControl && (
          <AutoLoadControl
            loadIcon={loadIcon ? loadIcon : undefined}
            autoLoading={autoLoading}
            batchSize={batchSize}
            loadingProgress={loadingProgress}
            disabled={!onLoadNextPage || !onStartAutoLoad}
            onBatchSizeChange={setBatchSize}
            onLoadNextPage={() => onLoadNextPage?.()}
            onStartAutoLoad={() => {
              const nextPages = calculateNextPagesToLoad();
              onStartAutoLoad?.(nextPages);
            }}
            onStopLoad={() => onStopLoading?.()}
          />
        )}
      </div>

      {/* 右侧：分页器（无快速跳转输入框） */}
      <Pagination
        current={current}
        pageSize={pageSize}
        total={total}
        onChange={(page) => onChange(page)}
        showSizeChanger={false}
        showQuickJumper={false}
        showTotal={() => ""}
        itemRender={itemRender}
      />
    </div>
  );
};
